import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { exportCsvUrl, getPin, getRole, getSheetWebhook, getStaffMembers, listCloses, setSheetWebhook, setStaffMembers, testSheetWebhook } from "@/lib/api";
import { downloadAuthedFile } from "@/lib/download";
import { formatDate } from "@/lib/format";
import { money } from "@/lib/format";
import { TILL_LABEL } from "@/lib/constants";
import { Download, Filter, ChevronRight, ChevronDown, AlertCircle, CalendarOff, Coins, Copy, Flag, Link2, ArrowLeftRight, Users, X as XIcon, Zap } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Dashboard() {
  const nav = useNavigate();
  const role = getRole();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [till, setTill] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [showStaffSummary, setShowStaffSummary] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCloses({
        till: till || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setItems(data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setForbidden(true);
      } else {
        toast.error("Couldn't load closes");
      }
    } finally {
      setLoading(false);
    }
  }, [till, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Per-staff roll-up computed from the currently loaded (filtered) closes.
  // A close with multiple names counts toward each person.
  const staffSummary = useMemo(() => {
    const map = {};
    for (const c of items) {
      const names = (c.who_worked || "").split(/\s*(?:,|&|\sand\s)\s*/i).map((s) => s.trim()).filter(Boolean);
      for (const n of names) {
        if (!map[n]) map[n] = { name: n, count: 0, net: 0, flagged: 0 };
        map[n].count += 1;
        map[n].net += Number(c.difference || 0);
        if (c.needs_review) map[n].flagged += 1;
      }
    }
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [items]);

  // Days in the last week (excluding today) with no end-of-day/shift-change
  // close at all — only when no filters are applied, and never before the
  // first close ever recorded.
  const missingDays = useMemo(() => {
    if (till || dateFrom || dateTo || !items.length) return [];
    const local = (iso) => new Date(iso).toLocaleDateString("en-CA");
    const closeDates = new Set(
      items.filter((c) => c.mode === "end_of_day" || c.mode === "shift_change").map((c) => local(c.created_at))
    );
    const earliest = items.map((c) => local(c.created_at)).sort()[0];
    const out = [];
    for (let i = 1; i <= 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const s = d.toLocaleDateString("en-CA");
      if (s < earliest) break;
      if (!closeDates.has(s)) out.push(s);
    }
    return out;
  }, [items, till, dateFrom, dateTo]);

  const downloadCsv = () => {
    // Use a real navigation to a Content-Disposition: attachment URL so iOS
    // Safari, Android Chrome, and desktop all save the file the normal way.
    const url = `${exportCsvUrl()}?pin=${encodeURIComponent(getPin())}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "till_trax_closes.csv";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch { /* node already detached */ } }, 1000);
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header onBack={() => nav(-1)} />
        <main className="max-w-md mx-auto px-5 py-10" data-testid="dashboard-forbidden">
          <div className="bg-white border border-[#E8E4DA] rounded-2xl p-6 text-center">
            <AlertCircle className="mx-auto text-[#D97706] mb-3" />
            <h2 className="text-xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Owner PIN required</h2>
            <p className="text-[#5C7566] text-sm mt-1">Sign in with the owner PIN to view all closes.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Past closes" onBack={() => nav("/")} />
      <main className="max-w-md mx-auto px-5 py-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>All closes</h2>
            <p className="text-xs text-[#5C7566]">{role === "owner" ? "Newest first." : "Filtered view."}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              data-testid="open-tips"
              onClick={() => nav("/tips")}
              className="h-10 px-3 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center gap-2 active:scale-95"
            >
              <Coins size={14} /> Tips
            </button>
            <button
              data-testid="open-bridge"
              onClick={() => nav("/bridge")}
              className="h-10 px-3 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center gap-2 active:scale-95"
            >
              <ArrowLeftRight size={14} /> Bridge
            </button>
            {role === "owner" && (
              <button
                data-testid="open-staff"
                onClick={() => setStaffOpen(true)}
                className="h-10 px-3 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center gap-2 active:scale-95"
              >
                <Users size={14} /> Staff
              </button>
            )}
            <button
              data-testid="open-live-sheet"
              onClick={() => setLiveOpen(true)}
              className="h-10 px-3 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center gap-2 active:scale-95"
            >
              <Link2 size={14} /> Live sheet
            </button>
            <button
              data-testid="export-csv"
              onClick={downloadCsv}
              title="Opens in Excel or Google Sheets (File → Import)"
              className="h-10 px-3 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center gap-2 active:scale-95"
            >
              <Download size={14} /> Excel / Sheets
            </button>
          </div>
        </div>

        <div className="bg-white border border-[#E8E4DA] rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#5C7566] text-xs tracking-[0.18em] uppercase">
            <Filter size={12} /> Filters
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select
              data-testid="filter-till"
              value={till}
              onChange={(e) => setTill(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            >
              <option value="">All tills</option>
              <option value="Pro Shop">Pro Shop</option>
              <option value="Grill">Grill</option>
              <option value="Masters Bar">Masters Bar</option>
            </select>
            <input
              data-testid="filter-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            />
            <input
              data-testid="filter-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            />
          </div>
        </div>

        {role === "owner" && missingDays.length > 0 && (
          <div data-testid="missing-days-banner" className="bg-[#D97706]/10 border border-[#D97706]/30 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-[#92520A] text-sm font-medium">
              <CalendarOff size={14} /> No close logged for:
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {missingDays.map((s) => (
                <span key={s} data-testid={`missing-day-${s}`} className="px-2.5 py-1 rounded-full bg-white border border-[#D97706]/30 text-xs text-[#92520A]">
                  {new Date(s + "T12:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-[#92520A]/80 mt-2">Start a close and use "Logging a past day?" on the first step to back-fill it.</p>
          </div>
        )}

        {role === "owner" && staffSummary.length > 0 && (
          <div className="bg-white border border-[#E8E4DA] rounded-2xl overflow-hidden" data-testid="staff-summary">
            <button
              data-testid="staff-summary-toggle"
              onClick={() => setShowStaffSummary((s) => !s)}
              className="w-full flex items-center justify-between p-4 active:scale-[0.99] transition-transform"
            >
              <span className="flex items-center gap-2 text-[#2B533E] text-sm font-medium"><Users size={14} /> Per-staff summary</span>
              <ChevronDown size={16} className={`text-[#5C7566] transition-transform ${showStaffSummary ? "rotate-180" : ""}`} />
            </button>
            {showStaffSummary && (
              <div className="divide-y divide-[#F0EDE4] border-t border-[#E8E4DA]">
                {staffSummary.map((s) => {
                  const balanced = Math.abs(s.net) < 0.005;
                  const tone = balanced ? "text-[#155F39]" : s.net < 0 ? "text-[#B91C1C]" : "text-[#92520A]";
                  const label = balanced ? "Even" : s.net < 0 ? `Short ${money(Math.abs(s.net))}` : `Over ${money(s.net)}`;
                  return (
                    <div key={s.name} data-testid={`staff-summary-${s.name}`} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-[#0D311E] text-sm truncate">{s.name}</p>
                        <p className="text-xs text-[#5C7566]">
                          {s.count} {s.count === 1 ? "close" : "closes"}
                          {s.flagged > 0 && <span className="text-[#B91C1C]"> · {s.flagged} flagged</span>}
                        </p>
                      </div>
                      <span className={`text-sm font-medium ${tone}`}>{label}</span>
                    </div>
                  );
                })}
                <p className="px-4 py-2 text-[11px] text-[#5C7566]">Net over/short across closes they worked. Closes with multiple names count for each person. Reflects current filters.</p>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#5C7566] py-10">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="dashboard-empty" className="text-center text-[#5C7566] py-10 bg-white border border-[#E8E4DA] rounded-2xl">
            No closes yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="dashboard-list">
            {items.map((c) => <Row key={c.id} c={c} onOpen={() => nav(`/close-detail/${c.id}`)} />)}
          </div>
        )}
        <LiveSheetDialog open={liveOpen} onOpenChange={setLiveOpen} />
        <StaffManager open={staffOpen} onOpenChange={setStaffOpen} />
      </main>
    </div>
  );
}

function Row({ c, onOpen }) {
  const diff = Number(c.difference || 0);
  const balanced = Math.abs(diff) < 0.005;
  const big = Math.abs(diff) > 10;
  const tone = balanced ? "bg-[#1B7B4B]/10 text-[#155F39]" : big ? "bg-[#B91C1C]/10 text-[#7F1D1D]" : "bg-[#D97706]/15 text-[#92520A]";
  const deptTotal = Object.entries(c.group_totals || {})
    .filter(([k]) => !k.startsWith("_label_"))
    .reduce((s, [, v]) => s + (Number(v) || 0), 0);
  const noDepts = (c.mode === "end_of_day" || c.mode === "shift_change") && Math.abs(deptTotal) < 0.005;
  return (
    <button
      data-testid={`row-${c.id}`}
      onClick={onOpen}
      className="w-full bg-white border border-[#E8E4DA] rounded-2xl p-4 flex items-center justify-between gap-3 hover:border-[#1A5D3A] text-left"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {c.needs_review && (
            <span data-testid={`flag-${c.id}`} className="inline-flex items-center text-[#B91C1C]" title="Flagged for review">
              <Flag size={12} fill="currentColor" />
            </span>
          )}
          <span className="text-[#0D311E] font-medium truncate">{TILL_LABEL[c.till] || c.till}</span>
          {noDepts && (
            <span data-testid={`no-depts-${c.id}`} className="px-1.5 py-0.5 rounded-md text-[10px] uppercase tracking-wide bg-[#D97706]/15 text-[#92520A] shrink-0">
              No depts
            </span>
          )}
        </div>
        <div className="text-xs text-[#5C7566] truncate">{formatDate(c.created_at)} · {String(c.mode).replace(/_/g, " ")}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={`px-2.5 py-1 rounded-full text-xs tabular-nums ${tone}`}>
          {balanced ? "Balanced" : `${diff > 0 ? "Over" : "Short"} ${money(Math.abs(diff))}`}
        </span>
        <ChevronRight size={16} className="text-[#5C7566]" />
      </div>
    </button>
  );
}

const APPS_SCRIPT_CODE = `function doPost(e) {
  var sheet = SpreadsheetApp.getActiveSpreadsheet().getSheets()[0];
  var d = JSON.parse(e.postData.contents);
  var headers = ["business_date","created_at","till","mode","moneris_total","tips","card_sales","register_drawer_total","register_card_line","register_cash_line","card_match","gst","pst","fd_pst","liq_pst","cash_counted","float_amount","cash_deposit","cheques","gift_cards","other","total_one","difference","needs_review","shortage_reason","shortage_reason_other","cash_tips_in_jar","float_counted_back","notes","submitted_by","who_worked","audit_summary","audit_at","id"];
  if (sheet.getLastRow() === 0) sheet.appendRow(headers);
  sheet.appendRow(headers.map(function (h) {
    var v = d[h];
    return v === undefined || v === null ? "" : v;
  }));
  return ContentService.createTextOutput(JSON.stringify({ ok: true }))
    .setMimeType(ContentService.MimeType.JSON);
}`;

function LiveSheetDialog({ open, onOpenChange }) {
  const formula = `=IMPORTDATA("${exportCsvUrl()}?pin=${encodeURIComponent(getPin())}")`;
  const [showInstant, setShowInstant] = useState(false);
  const [webhook, setWebhook] = useState("");
  const [busyW, setBusyW] = useState(false);
  useEffect(() => {
    if (open) getSheetWebhook().then((r) => setWebhook(r.url || "")).catch(() => {});
  }, [open]);
  const copy = async () => {
    const ok = await copyText(formula);
    if (ok) toast.success("Formula copied — paste into cell A1");
  };
  const copyCode = async () => {
    const ok = await copyText(APPS_SCRIPT_CODE);
    if (ok) toast.success("Script copied");
  };
  const saveAndTest = async () => {
    setBusyW(true);
    try {
      await setSheetWebhook(webhook.trim());
      if (webhook.trim()) {
        await testSheetWebhook();
        toast.success("Test row sent — check your sheet!");
      } else {
        toast.success("Instant push turned off");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach the sheet — check the URL");
    } finally {
      setBusyW(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#FDFBF7] border-[#E8E4DA] space-y-3 max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Live Google Sheet</DialogTitle>
        <DialogDescription className="text-[#5C7566] text-sm">
          Every saved close flows into one Google Sheet that refreshes automatically (about hourly). Set it up once:
        </DialogDescription>
        <ol className="list-decimal pl-5 text-sm text-[#2B533E] space-y-1.5">
          <li>Open a new <strong>Google Sheet</strong>.</li>
          <li>Click cell <strong>A1</strong> and paste the formula below.</li>
          <li>Press Enter — every close fills in and keeps updating.</li>
        </ol>
        <div className="bg-white border border-[#E8E4DA] rounded-xl p-3 text-xs font-mono break-all text-[#0D311E]" data-testid="live-sheet-formula">
          {formula}
        </div>
        <button
          data-testid="copy-live-formula"
          onClick={copy}
          className="w-full h-11 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center justify-center gap-2 active:scale-95"
        >
          <Link2 size={16} /> Copy formula
        </button>
        <p className="text-[11px] text-[#5C7566]">Anyone with this sheet can see the data — share it only with the owner/bookkeeper.</p>

        <button
          data-testid="toggle-instant-push"
          onClick={() => setShowInstant((s) => !s)}
          className="w-full flex items-center justify-between rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] px-4 h-11 text-sm text-[#2B533E]"
        >
          <span className="flex items-center gap-2"><Zap size={14} /> Instant updates (optional)</span>
          <ChevronDown size={16} className={`transition-transform ${showInstant ? "rotate-180" : ""}`} />
        </button>
        {showInstant && (
          <div className="space-y-3" data-testid="instant-push-setup">
            <p className="text-sm text-[#5C7566]">
              Want each close to appear in the sheet <strong>the second it's saved</strong>? One-time setup, no accounts or keys:
            </p>
            <ol className="list-decimal pl-5 text-sm text-[#2B533E] space-y-1.5">
              <li>In your Google Sheet: <strong>Extensions → Apps Script</strong>.</li>
              <li>Delete what's there, paste the script below, and save.</li>
              <li>Click <strong>Deploy → New deployment → Web app</strong>. Set <em>Execute as: Me</em> and <em>Who has access: Anyone</em>. Authorize when asked.</li>
              <li>Copy the <strong>Web app URL</strong> it gives you and paste it below.</li>
            </ol>
            <div className="bg-white border border-[#E8E4DA] rounded-xl p-3 text-[10px] font-mono break-all text-[#0D311E] max-h-36 overflow-y-auto whitespace-pre-wrap" data-testid="apps-script-code">
              {APPS_SCRIPT_CODE}
            </div>
            <button
              data-testid="copy-apps-script"
              onClick={copyCode}
              className="w-full h-10 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm flex items-center justify-center gap-2 active:scale-95"
            >
              <Copy size={14} /> Copy script
            </button>
            <input
              data-testid="webhook-url-input"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://script.google.com/macros/s/…/exec"
              className="w-full h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-xs text-[#0D311E] outline-none focus:border-[#1A5D3A]"
              autoComplete="off"
            />
            <button
              data-testid="webhook-save-test"
              onClick={saveAndTest}
              disabled={busyW}
              className="w-full h-11 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
            >
              <Zap size={14} /> {busyW ? "Sending test row…" : "Save & send a test row"}
            </button>
            <p className="text-[11px] text-[#5C7566]">New closes append instantly. Fixed counts update the app and the formula link — the instant sheet keeps the original row.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StaffManager({ open, onOpenChange }) {
  const [members, setMembers] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) getStaffMembers().then((r) => setMembers(r.members || [])).catch(() => {}); }, [open]);
  const add = () => {
    const n = draft.trim();
    if (n && !members.some((m) => m.name.toLowerCase() === n.toLowerCase())) setMembers([...members, { name: n, pin: "" }]);
    setDraft("");
  };
  const remove = (name) => setMembers(members.filter((m) => m.name !== name));
  const setPinFor = (name, pin) =>
    setMembers(members.map((m) => (m.name === name ? { ...m, pin: pin.replace(/[^0-9]/g, "").slice(0, 6) } : m)));
  const save = async () => {
    setSaving(true);
    try {
      const r = await setStaffMembers(members);
      setMembers(r.members || []);
      toast.success("Staff list saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save — owner PIN required");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#FDFBF7] border-[#E8E4DA] space-y-3 max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Staff & PINs</DialogTitle>
        <DialogDescription className="text-[#5C7566] text-sm">
          Names show as tap buttons on the "Who worked?" step. Give each person their own 4–6 digit PIN — when they sign in with it, the app records who did the close. Leave the PIN blank to keep using the shared staff PIN.
        </DialogDescription>
        <div className="space-y-2" data-testid="staff-manager-list">
          {members.map((m) => (
            <div key={m.name} className="flex items-center gap-2 bg-white border border-[#E8E4DA] rounded-xl p-2.5">
              <span className="flex-1 text-sm text-[#0D311E] truncate">{m.name}</span>
              <input
                data-testid={`staff-pin-${m.name}`}
                value={m.pin || ""}
                onChange={(e) => setPinFor(m.name, e.target.value)}
                placeholder="PIN"
                inputMode="numeric"
                className="w-20 h-9 rounded-lg border border-[#E8E4DA] bg-[#FDFBF7] px-2 text-sm text-center tabular-nums outline-none focus:border-[#1A5D3A]"
                autoComplete="off"
              />
              <button data-testid={`staff-remove-${m.name}`} onClick={() => remove(m.name)} aria-label={`Remove ${m.name}`} className="text-[#5C7566] hover:text-[#B91C1C] shrink-0">
                <XIcon size={14} />
              </button>
            </div>
          ))}
          {members.length === 0 && <span className="text-sm text-[#5C7566]">No names yet.</span>}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="staff-new-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add a name"
            className="flex-1 h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
            autoComplete="off"
          />
          <button data-testid="staff-add-btn" onClick={add} className="h-11 px-4 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm active:scale-95">Add</button>
        </div>
        <button
          data-testid="staff-save"
          onClick={save}
          disabled={saving}
          className="w-full h-11 rounded-xl bg-[#1A5D3A] text-white text-sm active:scale-95 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save staff list"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
